import {
  faChevronDown,
  faChevronRight,
  faChevronUp,
  faLocationDot,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FC, Key, ReactNode, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "../../../../store/store";
import CustomSwiper from "../../../../components/Slider/CustomSwiper";

// Interface for the round data coming from the API
interface Round {
  year: string;
  openingRank: string;
  closingRank: string;
  gender: string;
  category: string;
}

// Interface for the cutoff data per college (matches API structure)
interface CutOff {
  collegeName: string;
  collegeSlug: string;
  rank: string;
  fees: string;
  exam: string;
  examSlug: string;
  broucherUrl: string;
  program: string;
  rounds: Round[];
}

// Interface for the processed cutoff data by category (for CutOffTable)
interface CutOffByCategory {
  gender: string;
  caste: string; // Maps to category
  counsellingName: string; // Derived from exam or default
  openingRank: string;
  closingRank: string;
}

// Interface for the card data (aligned with API's cutOffs.data)
interface ICard {
  collegeName: string;
  collegeSlug: string; 
  program: string;
  rating?: string; // Optional, as not in API
  reviews?: number; // Optional, as not in API
  fees: string;
  round?: string; // Optional, as not in API
  rank: string;
  exam: string;
  examSlug: string; 
  broucherUrl: string;
  rounds: Round[];
  openPopup: () => void;
}

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  return isMobile;
};

// Component to render the table
const CutOffTable: FC<{
  cutOffData: CutOff[];
  className?: string;
  justify?: "left" | "center";
}> = ({ cutOffData, className, justify = "center" }) => {
  const categories: string[] = [];
  const cutOffsByCategory: { [key: string]: CutOffByCategory } = {};

  cutOffData?.forEach((cutOff) => {
    if (!cutOff?.rounds) return;

    cutOff.rounds.forEach((round) => {
      const categoryKey = `${round.gender}-${round.category}-${
        cutOff.exam || "Default"
      }`;

      if (!categories.includes(categoryKey)) {
        categories.push(categoryKey);
      }

      cutOffsByCategory[categoryKey] = {
        gender: round.gender || "N/A",
        caste: round.category || "N/A",
        counsellingName: cutOff.exam || "Default Counselling",
        openingRank: round.openingRank || "-",
        closingRank: round.closingRank || "-",
      };
    });
  });

  const justifyClass = justify === "left" ? "text-left" : "text-center";

  return (
    <div className={`mt-4 overflow-x-auto ${className || ""}`}>
      <table className="min-w-full bg-white text-xs sm:text-sm border border-[#A3A2AC] college-predictor-table">
        <thead className="sticky top-0 bg-white border-b">
          <tr>
            <th
              className={`px-2 sm:px-4 py-2 whitespace-nowrap ${justifyClass}`}
            >
              Gender
            </th>
            <th
              className={`px-2 sm:px-4 py-2 whitespace-nowrap ${justifyClass}`}
            >
              Category
            </th>
            <th
              className={`px-2 sm:px-4 py-2 whitespace-nowrap ${justifyClass}`}
            >
              Counselling
            </th>
            <th
              className={`px-2 sm:px-4 py-2 whitespace-nowrap ${justifyClass}`}
            >
              Opening Rank
            </th>
            <th
              className={`px-2 sm:px-4 py-2 whitespace-nowrap ${justifyClass}`}
            >
              Closing Rank
            </th>
          </tr>
        </thead>
        <tbody>
          {categories.map((categoryKey) => {
            const { gender, caste, counsellingName, openingRank, closingRank } =
              cutOffsByCategory[categoryKey];
            return (
              <tr key={categoryKey} className="hover:bg-gray-50">
                <td
                  className={`px-2 sm:px-4 py-2 whitespace-nowrap ${justifyClass}`}
                >
                  {gender}
                </td>
                <td
                  className={`px-2 sm:px-4 py-2 whitespace-nowrap ${justifyClass}`}
                >
                  {caste}
                </td>
                <td
                  className={`px-2 sm:px-4 py-2 whitespace-nowrap ${justifyClass}`}
                >
                  {counsellingName}
                </td>
                <td
                  className={`px-2 sm:px-4 py-2 whitespace-nowrap ${justifyClass}`}
                >
                  {openingRank}
                </td>
                <td
                  className={`px-2 sm:px-4 py-2 whitespace-nowrap ${justifyClass}`}
                >
                  {closingRank}
                </td>
              </tr>
            );
          })}
          {categories.length === 0 && (
            <tr>
              <td colSpan={5} className={`px-2 sm:px-4 py-2 ${justifyClass}`}>
                No cutoff data available
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

const CutOffs = () => {
  const [showPopup, setShowPopup] = useState(false);
  const isMobile = useIsMobile();

  const data = useSelector(
    (state: RootState) =>
      state.collegePredictorIntermediate.landingData?.cutOffs
  );
  console.log("CutOffs", data);

  // Group cutOffs.data by collegeName to ensure unique colleges
  const groupedData = data?.data
    ? Object.values(
        data.data.reduce((acc: { [key: string]: CutOff }, item: CutOff) => {
          if (!acc[item.collegeName]) {
            acc[item.collegeName] = {
              ...item,
              rounds: [],
            };
          }
          acc[item.collegeName].rounds.push(...item.rounds);
          return acc;
        }, {})
      )
    : [];

  const chunkArray = (array: any[], size: number) => {
    if (!array) return [];
    return array.reduce((chunks, item, index) => {
      const chunkIndex = Math.floor(index / size);
      if (!chunks[chunkIndex]) {
        chunks[chunkIndex] = [];
      }
      chunks[chunkIndex].push(item);
      return chunks;
    }, []);
  };

  const swiperItems = isMobile
    ? groupedData.map((item: any, index: number) => (
        <div key={index} className="px-2">
          <Card
            collegeName={item.collegeName}
            collegeSlug={item.collegeSlug} 
            program={item.program}
            rating="4" // Default value (not in API)
            reviews={0} // Default value (not in API)
            fees={item.fees}
            round="Round 1" // Default value (not in API)
            rank={item.rank}
            exam={item.exam}
            examSlug={item.examSlug} 
            broucherUrl={item.broucherUrl}
            rounds={item.rounds}
            openPopup={() => setShowPopup(true)}
          />
        </div>
      ))
    : chunkArray(groupedData, 4).map(
        (group: CutOff[], groupIndex: Key | null | undefined) => (
          <div
            key={groupIndex}
            className="grid grid-cols-2 gap-4 max-sm:grid-cols-1"
          >
            {group.map((item: CutOff, index: number) => (
              <Card
                key={index}
                collegeName={item.collegeName}
                collegeSlug={item.collegeSlug} 
                program={item.program}
                rating="4" // Default value
                reviews={0} // Default value
                fees={item.fees}
                round="Round 1" // Default value
                rank={item.rank}
                exam={item.exam}
                examSlug={item.examSlug} 
                broucherUrl={item.broucherUrl}
                rounds={item.rounds}
                openPopup={() => setShowPopup(true)}
              />
            ))}
          </div>
        )
      );

  return (
    <section className="py-12 max-sm:py-7 bg-[#E9F3EE] text-black">
      <div className="container mx-auto">
        <h3 className="text-2xl text-center font-semibold">{data?.title}</h3>
        <div className="collegePredictorSlider max-sm:px-5">
          {groupedData.length > 0 && (
            <CustomSwiper
              items={swiperItems}
              className="mt-6"
              navigation={"bottom"}
              autoplay={true}
              breakpoints={{
                320: { slidesPerView: 1, spaceBetween: 20 },
                768: { slidesPerView: 1, spaceBetween: 20 },
                1024: { slidesPerView: 2, spaceBetween: 20 },
                1280: { slidesPerView: 2, spaceBetween: 30 },
              }}
              progressBar={true}
            />
          )}
        </div>
      </div>
    </section>
  );
};

export default CutOffs;

const Card: FC<ICard> = ({
  collegeName,
  collegeSlug,
  program,
  rating,
  reviews,
  fees,
  round,
  rank,
  exam,
  examSlug,
  rounds,
  broucherUrl,
  openPopup,
}) => {
  const [showAllRounds, setShowAllRounds] = useState(true);
  const toggleShowAllRounds = () => setShowAllRounds((prev) => !prev);
  const isMobile = useIsMobile();

  const cutOffData: CutOff[] = [
    {
      collegeName,
      collegeSlug,
      rank,
      fees,
      exam,
      examSlug,
      broucherUrl,
      program,
      rounds,
    },
  ];

  return (
    <div className="bg-white border border-grey-3">
      <div className="p-4 border-b border-grey-3">
        <Link
          to={`/college-details/${collegeSlug}`} 
          className="text-lg font-semibold line-clamp-2 md:line-clamp-1"
        >
          {collegeName}
        </Link>
        <div className="flex max-sm:flex-col gap-2 justify-between mt-3 text-xs text-grey">
          <span>
            <Link to={`/college-details/${collegeSlug}`} className="px-2"> 
              Placement
            </Link>{" "}
            |{" "}
            <Link to={`/college-details/${collegeSlug}`} className="px-2"> 
              CutOff
            </Link>
          </span>
          {/* Commented out View Details button 
          <Link to={`/college-details/${collegeSlug}`}>
            View Details <FontAwesomeIcon icon={faChevronRight} size="xs" />
          </Link>
          */}
        </div>
      </div>
      <div className="p-4">
        <div className="flex flex-wrap max-sm:flex-col justify-between gap-2 max-sm:min-h-[58px]">
          {/* <Link
            to={`/course-details/${program}`}
            className="text-sm font-semibold text-blue"
          > */}
          <div className="text-sm font-semibold text-blue">
            {program}
          </div>
          {/* </Link> */}
          {/* Commented out View Details button 
          <Link to={`/course-details/${program}`} className="text-xs text-grey">
            View Details <FontAwesomeIcon icon={faChevronRight} size="xs" />
          </Link>
          */}
        </div>
        <div className="mt-4 text-xs">
          <span className="font-semibold">{rating || "N/A"}</span>{" "}
          <span
            style={{
              background: `linear-gradient(90deg, #FFC700 ${
                (parseInt(rating || "0") * 100) / 5
              }%, #C8C8C8 ${((5 - parseInt(rating || "0")) * 100) / 5}%)`,
              WebkitBackgroundClip: "text",
              color: "transparent",
            }}
          >
            ★★★★★
          </span>{" "}
          ({reviews || 0}) | ₹ {fees}
        </div>
        <div className="flex max-sm:flex-row gap-4 md:items-center justify-between mt-4 text-xs md:text-sm text-grey">
          <span>
            <Link
              to={`/exam-details/${examSlug}`} 
              className="p-1 border border-grey"
            >
              {exam}
            </Link>{" "}
            {round || "N/A"} | {rank}
          </span>
          <button className="px-2 py-1 bg-grey-1" onClick={toggleShowAllRounds}>
            All Rounds{" "}
            <FontAwesomeIcon
              icon={showAllRounds ? faChevronUp : faChevronDown}
              className="text-blue"
            />
          </button>
        </div>
        {showAllRounds && (
          <CutOffTable
            cutOffData={cutOffData}
            className="mt-6"
            justify={isMobile ? "left" : "center"}
          />
        )}
      </div>
    </div>
  );
};
